import { ShieldCheck, FileText, Zap } from 'lucide-react';

const nodes = [
  {
    icon: ShieldCheck,
    label: 'Pre-Op Authorization',
    description: 'Payer-specific rules, subset selection, and coverage verification.',
    angle: 270,
  },
  {
    icon: FileText,
    label: 'Intra-Op Documentation',
    description: 'Surgeon dictation, proof mapping, and real-time reconciliation.',
    angle: 30,
  },
  {
    icon: Zap,
    label: 'Post-Op Billing',
    description: 'CPT sequencing, modifier logic, and audit-ready export.',
    angle: 150,
  },
];

export function ClosedLoopGraphic() {
  const size = 320;
  const center = size / 2;
  const radius = 120;
  const nodeRadius = 42;

  const positions = nodes.map((node) => {
    const rad = (node.angle * Math.PI) / 180;
    return {
      x: center + radius * Math.cos(rad),
      y: center + radius * Math.sin(rad),
    };
  });

  const lineSegments = positions.map((pos, i) => {
    const next = positions[(i + 1) % positions.length];
    const dx = next.x - pos.x;
    const dy = next.y - pos.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const ux = dx / dist;
    const uy = dy / dist;
    return {
      x1: pos.x + ux * nodeRadius,
      y1: pos.y + uy * nodeRadius,
      x2: next.x - ux * nodeRadius,
      y2: next.y - uy * nodeRadius,
    };
  });

  return (
    <div className="relative flex items-center justify-center">
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="w-[280px] h-[280px] sm:w-[320px] sm:h-[320px] md:w-[400px] md:h-[400px]"
        fill="none"
        overflow="visible"
      >
        <defs>
          <filter id="glow-loop">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {positions.map((pos, i) => (
          <g key={`node-${i}`}>
            <circle
              cx={pos.x}
              cy={pos.y}
              r={nodeRadius}
              className="fill-card"
              stroke="hsl(199 98% 39%)"
              strokeWidth="1.5"
              strokeOpacity="0.4"
            />
            <circle
              cx={pos.x}
              cy={pos.y}
              r={nodeRadius}
              fill="none"
              stroke="hsl(199 98% 39%)"
              strokeWidth="1"
              strokeOpacity="0.15"
              strokeDasharray="3 3"
            >
              <animateTransform
                attributeName="transform"
                type="rotate"
                from={`0 ${pos.x} ${pos.y}`}
                to={`360 ${pos.x} ${pos.y}`}
                dur="20s"
                repeatCount="indefinite"
              />
            </circle>
          </g>
        ))}

        {lineSegments.map((seg, i) => (
          <line
            key={`line-${i}`}
            x1={seg.x1}
            y1={seg.y1}
            x2={seg.x2}
            y2={seg.y2}
            stroke="hsl(199 98% 39%)"
            strokeWidth="1.5"
            strokeOpacity="0.5"
            strokeDasharray="6 4"
          />
        ))}

        {lineSegments.map((seg, i) => (
          <circle key={`pulse-${i}`} r="3" fill="hsl(199 98% 39%)">
            <animateMotion
              dur={`${3 + i * 0.5}s`}
              repeatCount="indefinite"
              path={`M${seg.x1},${seg.y1} L${seg.x2},${seg.y2}`}
            />
          </circle>
        ))}
      </svg>

      {nodes.map((node, i) => {
        const pos = positions[i];
        const pctX = (pos.x / size) * 100;
        const pctY = (pos.y / size) * 100;

        return (
          <div
            key={node.label}
            className="absolute flex flex-col items-center pointer-events-none"
            style={{
              left: `${pctX}%`,
              top: `${pctY}%`,
              transform: 'translate(-50%, -50%)',
            }}
          >
            <node.icon className="h-5 w-5 md:h-6 md:w-6 text-primary mb-1" strokeWidth={1.5} />
            <span className="text-[10px] md:text-xs font-semibold text-foreground text-center whitespace-nowrap">
              {node.label}
            </span>
          </div>
        );
      })}

      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="text-center">
          <span className="text-xs md:text-sm font-bold text-primary tracking-wider uppercase">
            Closed
          </span>
          <br />
          <span className="text-xs md:text-sm font-bold text-primary tracking-wider uppercase">
            Loop
          </span>
        </div>
      </div>
    </div>
  );
}
